// src/contexts/ImporterContext.js

import React, { createContext, useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import useImporterService from '../services/ImporterService'
import { getUserInfo } from '../components/auth/authorisation'
import { Loader } from 'src/components/common/Loader'

export const ImporterContext = createContext()

const SELECTED_IMPORTER_ID_KEY = 'selectedImporterId'
const SELECTED_IMPORTER_OWNER_KEY = 'selectedImporterIdOwner'

const getStoredSelectedImporterId = () => {
  const value = localStorage.getItem(SELECTED_IMPORTER_ID_KEY)
  return value && value !== 'null' && value !== 'undefined' ? value : null
}

const getStoredSelectedImporterOwner = () => {
  const value = localStorage.getItem(SELECTED_IMPORTER_OWNER_KEY)
  return value && value !== 'null' && value !== 'undefined' ? value : null
}

const setStoredSelectedImporterId = (selectedImporterId, ownerKey) => {
  if (selectedImporterId) {
    localStorage.setItem(SELECTED_IMPORTER_ID_KEY, selectedImporterId)

    if (ownerKey) {
      localStorage.setItem(SELECTED_IMPORTER_OWNER_KEY, ownerKey)
    }

    return
  }

  localStorage.removeItem(SELECTED_IMPORTER_ID_KEY)
  localStorage.removeItem(SELECTED_IMPORTER_OWNER_KEY)
}

const getUserStorageOwnerKey = (userInfo) => {
  if (!userInfo) {
    return null
  }

  return userInfo.id || userInfo.email || userInfo.signInName || null
}

const distinctValues = (values) => {
  return [...new Set(values.filter(Boolean))]
}

export const ImporterProvider = ({ children }) => {
  const { getByTenantId, getByTenantIds } = useImporterService()

  const [state, setState] = useState({
    importerIds: [],
    selectedImporterId: getStoredSelectedImporterId(),
    defaultImporterId: null,
    importer: null,
    importers: [],
    isRegistered: undefined,
    isLoading: true,
    importerLoadError: false,
  })

  const setSelectedImporterId = (selectedImporterId) => {
    const currentOwner = getStoredSelectedImporterOwner()

    setStoredSelectedImporterId(selectedImporterId, currentOwner)

    setState((prevState) => ({
      ...prevState,
      selectedImporterId,
    }))

    window.location.reload()
  }

  useEffect(() => {
    const fetchImporterData = async () => {
      try {
        const userInfo = await getUserInfo()

        if (!userInfo) {
          setState((prevState) => ({
            ...prevState,
            importerIds: [],
            selectedImporterId: null,
            defaultImporterId: null,
            importer: null,
            importers: [],
            isRegistered: false,
            isLoading: false,
            importerLoadError: false,
          }))

          return
        }

        const ownerKey = getUserStorageOwnerKey(userInfo)
        const storedOwnerKey = getStoredSelectedImporterOwner()

        let selectedImporterId = getStoredSelectedImporterId()

        /*
         * If a different B2C user has logged in, never reuse the old user's selected importer.
         */
        if (selectedImporterId && storedOwnerKey && ownerKey && storedOwnerKey !== ownerKey) {
          selectedImporterId = null
          setStoredSelectedImporterId(null, null)
        }

        const importerIds = Array.isArray(userInfo.importerIds) ? userInfo.importerIds : []

        const defaultImporterId =
          userInfo.defaultImporterId || userInfo.defaultImporterId2 || importerIds[0] || null

        const validImporterIdsForCurrentUser = distinctValues([defaultImporterId, ...importerIds])

        /*
         * If selectedImporterId exists but is not valid for this current user,
         * clear it and fall back to the current user's default importer.
         */
        if (
          selectedImporterId &&
          validImporterIdsForCurrentUser.length > 0 &&
          !validImporterIdsForCurrentUser.includes(selectedImporterId)
        ) {
          selectedImporterId = null
          setStoredSelectedImporterId(null, null)
        }

        if (!selectedImporterId && defaultImporterId) {
          selectedImporterId = defaultImporterId
          setStoredSelectedImporterId(selectedImporterId, ownerKey)
        }

        /*
         * No importer ID means the user has no importer claim yet.
         * That is a registration state, not an API failure.
         */
        if (!selectedImporterId) {
          setState((prevState) => ({
            ...prevState,
            importerIds,
            selectedImporterId: null,
            defaultImporterId,
            importer: null,
            importers: [],
            isRegistered: false,
            isLoading: false,
            importerLoadError: false,
          }))

          return
        }

        let importers = []

        if (importerIds.length > 0) {
          const importersResponse = await getByTenantIds([...importerIds], defaultImporterId)
          importers = importersResponse?.data || []
        }

        const importerResponse = await getByTenantId(selectedImporterId, selectedImporterId)
        const importer = importerResponse?.data || null

        /*
         * Keep selectedImporterId in localStorage even if importer is null.
         * Null importer means not registered yet, but the register page still
         * needs this ID to load the setup form.
         */
        setStoredSelectedImporterId(selectedImporterId, ownerKey)

        setState((prevState) => ({
          ...prevState,
          importerIds,
          selectedImporterId,
          defaultImporterId,
          importer,
          importers,
          isRegistered: !!importer,
          isLoading: false,
          importerLoadError: false,
        }))
      } catch (error) {
        console.error('Failed to load importer data', error)

        /*
         * API/network failure is not the same as not registered.
         * LoginHandler should send this to /app/500.
         */
        setState((prevState) => ({
          ...prevState,
          importer: null,
          importers: [],
          isRegistered: undefined,
          isLoading: false,
          importerLoadError: true,
        }))
      }
    }

    fetchImporterData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ImporterContext.Provider value={{ ...state, setSelectedImporterId }}>
      {children}
      <Loader show={state.isLoading} message=" " />
    </ImporterContext.Provider>
  )
}

ImporterProvider.propTypes = {
  children: PropTypes.node,
}

export const useImporter = () => {
  const context = useContext(ImporterContext)

  if (!context) {
    throw new Error('useImporter must be used within an ImporterProvider')
  }

  return context
}
